import {Group} from '../../../graphql/groups.datatypes';
import {makeStyles} from '@material-ui/styles';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {Link, NavLink, RouteComponentProps} from 'react-router-dom';
import useReactRouter from 'use-react-router';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {Page} from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import {
  colors,
  Theme,
} from '@material-ui/core';
import {GroupDetailsTotals} from './GroupDetailsTotals';
import {GroupDetailsTabs} from './GroupDetailsTabs';
import getGroupWithQuery from '../getGroupWithQuery.api';
import {checkIsAdmin} from '../../../utils/checkIsAdmin';
import {checkEditLevel} from '../../../utils/checkEditLevel';
import getGroupByRefWithQuery from '../getGroupByRefWithQuery.api';
import {gqlGroupBasicQuery} from '../getGroupQueries.api';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  container: {
    marginTop: theme.spacing(3),
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  card: {},
  saveButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  deleteButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: theme.palette.error.light,
    '&:hover': {
      backgroundColor: theme.palette.error.main
    }
  },
  cloneButton: {
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: colors.green[400],
    '&:hover': {
      backgroundColor: colors.green[700]
    }
  },
  errorIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.error.main,
  },
  marginBottom: {
    paddingLeft: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  tabs: {
    marginTop: theme.spacing(3)
  },
  linkButton: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      marginBottom: 10,
    }
  },
  buttonWrapper: {
    [theme.breakpoints.down('md')]: {

      flexWrap: 'wrap',
      marginBottom: 10,
    },
  },
  buttonHeader: {
    [theme.breakpoints.down('md')]: {
      flex: 'auto',
      marginTop: 10,
      marginRight: 0,
      flexWrap: 'wrap',
    }
  }
}));

export const getBlankGroup = (): Group => {
    return {
        __typename: 'Group',
        id: -1,
        name: '',
        description: '',
        editing_level: 0,
        booking_form_url: '',
        tour_resource_id: -1,
        hotel_resource_id: -1,
        default_authorize_members: false,
        max_additional_nights: 0,
        convenience_fee: 0,
        credit_card_fee: 0,
        comp_dollar_value: 0,
        comp_ratio: 0,
        comp_final_value: 0,
        comp_dollar_estimate: 0,
        description_resort: '',
        description_whats_included: '',
        description_checkin: '',
        description_insurance: '',
        start_date: undefined,
        end_date: undefined,
        admissions_cutoff: undefined,
        booking_tool_edit_cutoff: undefined,
        start_date_additional: undefined,
        end_date_additional: undefined,
        locked: false,
        disabled: false,
        validated: false,
        invoice_document_id: -1,

        cancelled_members_count: 0,
        members_count: 0,
        performers_count: 0,

        primaryLeader: undefined,
        members: [],
        hotel: undefined,
        tour: undefined,
    };
};

export const MyGroupDetails = () => {
  const {history, match}: RouteComponentProps<{ ref: string }> = useReactRouter();
  const classes = useStyles();
  const {ref} = match.params;

  const gqlGroupBasic = `
        id
        name
        tour_resource_id
        editing_level
    `;

  const groupBasicData = getGroupByRefWithQuery(ref, gqlGroupBasic);
  const groupBasic = groupBasicData && !groupBasicData.loading && groupBasicData.data && groupBasicData.data.groupByRef;

  const {enqueueSnackbar} = useSnackbar();
  const title = 'Group Details' + (groupBasic ? ': ' + groupBasic.name : '');

  //Check if we can edit the form
  const isAdmin = checkIsAdmin();
  const checkLevel = checkEditLevel();
  const canEdit = groupBasic ? checkLevel(groupBasic.id, groupBasic.editing_level!) : false;

  useEffect(() => {
    if (groupBasicData.error) {
      enqueueSnackbar(`There was an error loading Group ${ref}`, {variant: 'error'});
      history.push('/mygroups');
    }
  }, [groupBasicData.error]);

  return (
      <Page className={classes.root} title={title}>
        <PageHeader section="groups" title={title}/>
        <LoadingOverlay loading={groupBasicData.loading}>
          {groupBasic && (<GroupDetailsTotals group={groupBasic} isAdmin={isAdmin} canEdit={canEdit}/>)}
        </LoadingOverlay>
        <LoadingOverlay loading={groupBasicData.loading}>
          {groupBasic && (<GroupDetailsTabs group={groupBasic} isAdmin={isAdmin} canEdit={canEdit}/>)}
        </LoadingOverlay>
      </Page>
  );
};

const GroupDetails = () => {
  const {history, match}: RouteComponentProps<{ id: string }> = useReactRouter();
  const classes = useStyles();
  const {id} = match.params;
  const newGroup = id === 'new';
  const {enqueueSnackbar} = useSnackbar();

  const groupBasicData = newGroup ? {loading: false, data: {group: getBlankGroup()}, error: null} : getGroupWithQuery(Number(id), gqlGroupBasicQuery);
  const groupBasic = groupBasicData && !groupBasicData.loading && groupBasicData.data && groupBasicData.data.group;

  const title = 'Group Details' + (groupBasic ? ': ' + groupBasic.name : '');

  useEffect(() => {
    if (groupBasicData.error) {
      enqueueSnackbar(`There was an error loading Group ${id}`, {variant: 'error'});
      history.push('/groups');
    }
  }, [groupBasicData.error]);

  return (
      <Page className={classes.root} title={title}>
        <PageHeader section="groups" title={title}/>
        <LoadingOverlay loading={groupBasicData.loading}>
          {groupBasic && (<GroupDetailsTotals group={groupBasic} isAdmin={true} canEdit={true}/>)}
        </LoadingOverlay>

        <LoadingOverlay loading={groupBasicData.loading}>
          {groupBasic && (<GroupDetailsTabs group={groupBasic} isAdmin={true} canEdit={true}/>)}
        </LoadingOverlay>
      </Page>
  );
};

export default GroupDetails;
